<template>
  <div class="flex flex-col w-full mb-3">
    <div class="flex w-full gap-1 mb-2">
      <div class="w-1/2">
        <TextInput
          :validate-on-input="true"
          :value="label"
          :label="$t('Component label')"
          :classes="'w-full'"
          input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="name"
          type="text"
          :placeholder="$t('Component label')"
          @update:modelValue="
            (e) => {
              label = e
              setComponentData()
            }
          " />
      </div>
      <div class="w-1/2">
        <MultiSelectInput
          v-model:current-items="selected_categories"
          :value="selected_categories"
          :disabled="false"
          name="Categories"
          :items="categories"
          label-attr="name"
          value-attr="id"
          :allow-null="true"
          :label="$t('Type')"
          classes="w-full"
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
          :placeholder="$t('Categories')"
          :show-label="false" />
      </div>
    </div>
    <div class="flex w-full gap-1">
      <div class="w-1/2">
        <SelectInput
          v-model="linked_to"
          :disabled="disabled_connect_to_selection"
          name="select_input"
          :allow-null="false"
          :items="data_source_options"
          :is-box-background="true"
          :value="linked_to"
          label-attr="name"
          value-attr="value"
          :placeholder="$t('Data source')"
          :label="$t('Data source')"
          class="w-full"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
      </div>
      <div class="w-1/2">
        <CompaniesSelect
          v-if="linked_to == 'specific_record'"
          :multi-select="false"
          :disabled="false"
          :companies="linked_record ? [linked_record] : []"
          :label="$t('Selected Record')"
          class="w-full z-50"
          @changed="
            ($event) => {
              if ($event) {
                linked_record = $event[0]?.id
                page_identifier = `${$route.params.object_type}>${$event[0]?.id}`
                if ($route.name == 'CompanyProfile' || $route.name == 'ObjectProfile') {
                  page = 'record_page'
                } else {
                  page = 'this_page'
                }
                setComponentData()
              }
            }
          " />
      </div>
    </div>
  </div>

  <div class="flex flex-row justify-between w-full gap-2 mt-2">
    <div class="w-1/2 m-0 cancel-btn" @click="is_loading ? null : $emit('cancel')">
      {{ $t("Cancel") }}
    </div>
    <button
      :class="[!label ? 'opacity-50 pointer-events-none' : '']"
      :disable="!label || !notes.length"
      type="button"
      class="w-1/2 primary-btn"
      @click="updateNotes()">
      {{ is_loading ? $t("Loading...") : notesToEdit ? $t("Update Notes") : $t("Create Notes") }}
    </button>
  </div>
</template>

<script>
import SelectInput from "@/components/shared/SelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import dataService from "@/services/data.service"

export default {
  components: { SelectInput, TextInput, MultiSelectInput, CompaniesSelect },
  props: {
    notesToEdit: {
      type: Object,
      default: null
    }
  },
  emits: ["cancel", "setComponentData", "submitForm"],
  data() {
    return {
      notes: [],
      label: null,
      categories: [],
      selected_categories: [],
      is_loading: false,

      linked_record: null,
      page: null,
      linked_to: null,
      data_source_options: [],
      page_identifier: "all",
      disabled_connect_to_selection: false,
      fetched_all_notes: false
    }
  },
  watch: {
    fetched_all_notes() {
      if (this.fetched_all_notes) {
        let arr = this.notes.sort((objA, objB) => Number(new Date(objB.datetime_created)) - Number(new Date(objA.datetime_created)))
        this.notes = this.$utils.clone(arr)
      }
    },
    notes: {
      deep: true,
      handler() {
        this.$emit("setComponentData", {
          required_filled: this.notes.length && this.label && this.linked_record,
          recordId: this.linked_record,
          categories: this.selected_categories,
          notes: this.notes,
          label: this.label,
          connectTo: this.page,
          linked_to: this.linked_to,
          page_identifier: this.page_identifier
        })
      }
    },
    linked_to() {
      if (this.linked_to == "this_record") {
        this.linked_record = this.$route.params.cid
        this.page_identifier = `${this.$route.params.object_type}>${this.$route.params.cid}`
        this.page = "record_page"
      } else if (this.linked_to == "specific_record") {
        this.linked_record = null
      } else if (this.linked_to == "all_records") {
        this.page = "this_page"
        this.linked_record = "this_page"
        this.page_identifier = "all"
      } else {
        this.page = "this_page"
        this.linked_record = "this_page"
        this.page_identifier = "all"
      }
      this.setComponentData()
    },
    selected_categories() {
      this.setComponentData()
    }
  },

  created() {
    dataService.getNoteCategories().then((res) => {
      this.categories = res.data.results
    })
    if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
      this.data_source_options = [
        { name: this.$t("This record"), value: "this_record" },
        { name: this.$t("Specific record"), value: "specific_record" }
      ]
      this.linked_to = "this_record"
      this.linked_record = this.$route.params.cid
    } else {
      this.data_source_options = [
        { name: this.$t("This page"), value: "this_page" },
        { name: this.$t("Specific record"), value: "specific_record" },
        { name: this.$t("All records"), value: "all_records" }
      ]
      this.linked_to = "this_page"
      this.linked_record = null
    }
    if (this.notesToEdit) {
      this.disabled_connect_to_selection = true

      this.linked_to = this.notesToEdit.props.component_props?.linked_to
      const notes_ids = this.$utils.extractValue(this.notesToEdit.props.component_props.notes, "", this.$route)
        ? this.$utils.extractValue(this.notesToEdit.props.component_props.notes, "", this.$route)
        : []
      this.fetched_all_notes = notes_ids.length ? false : true
      this.notes = []
      notes_ids.forEach((att_id, i) => {
        if (this.$store.state.portfolio.linked_notes.find((att) => att.id == att_id)) {
          this.notes.push(this.$store.state.portfolio.linked_notes.find((att) => att.id == att_id))

          if (i == notes_ids?.length - 1) {
            this.fetched_all_notes = true
          }
        } else {
          dataService.getNote(att_id).then((res) => {
            this.notes.push(res.data)
            if (i == notes_ids?.length - 1) {
              setTimeout(() => {
                this.fetched_all_notes = true
              }, 200)
            }
          })
        }
      })

      this.label = this.$utils.extractValue(this.notesToEdit.props.component_props.label, "", this.$route)
      this.selected_categories = this.notesToEdit.props.component_props?.categories ? this.notesToEdit.props.component_props.categories : []
    } else {
      this.fetched_all_notes = true
    }
  },

  methods: {
    setComponentData() {
      this.$emit("setComponentData", {
        required_filled: this.label && this.linked_record,
        recordId: this.linked_record,
        categories: this.selected_categories,
        notes: this.notes,
        label: this.label,
        connectTo: this.page,
        linked_to: this.linked_to,
        page_identifier: this.page_identifier
      })
    },
    updateNotes() {
      this.$emit("submitForm", {
        id: this.notesToEdit?.id,
        label: this.label,
        recordId: this.linked_record,
        categories: this.selected_categories,
        notes: this.notes,
        component_type: "notes",
        connectTo: this.page,
        linked_to: this.linked_to,
        page_identifier: this.page_identifier
      })
    }
  }
}
</script>
